import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';

import {
  Container,
  ContainerLeft,
  ContainerRight,
  FormContainer,
  FormContent,
  FormTitle,
  Logo,
  FormSubtitle,
  FormLink,
  FormMessage,
} from 'modules/auth/components';
import logo from 'assets/images/Logo.webp';
import localizedStrings from 'localization';
import { emailVerificationSchema } from 'modules/auth/validationSchemas';
import { AppDispatch, RootState } from 'redux/typings';
import {
  emailSelector,
  selectEmail,
} from 'modules/auth/selectors';
import { clearSignup, resendCode, verifyCode } from 'modules/auth/actions';
import { LOADING_SCREEN } from 'routes';
import { EmailPayload, VerifyPayload } from 'modules/auth/typings';
import useActionStatus from 'hooks/useActionStatus';
import useErrorMessage from 'hooks/useErrorMessage';

type dataParamsType = {
  verificationCode: string | null;
  name: string | null;
  email: string | null;
};

const EmailVerification = () => {
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [hasVerified, setHasVerified] = useState(false);
  const [, hasVerifiedCode] = useActionStatus(verifyCode);
  const [, setErrorMessage] = useErrorMessage();

  const dispatch = useDispatch<AppDispatch>();
  const { search } = useLocation();
  const history = useHistory();

  const signupEmail = useSelector<RootState, string>(emailSelector) as string | undefined;
  const jwtEmail = useSelector(selectEmail) as string | undefined;

  const dataParams: dataParamsType = useMemo(() => {
    const urlParams = new URLSearchParams(search);
    return {
      verificationCode: urlParams.get('verificationCode'),
      name: urlParams.get('name'),
      email: urlParams.get('email'),
    };
  }, [search]);
  const isDataParamsExist = useMemo(
    () =>
      Boolean(dataParams.verificationCode) &&
      Boolean(dataParams.email) &&
      Boolean(dataParams.name),
    [dataParams],
  );

  const onGoBack = useCallback(() => {
    dispatch(clearSignup());
    history.goBack();
  }, [dispatch, history]);

  const onResend = useCallback(() => {
    const email = signupEmail || jwtEmail;
    if (email && typeof email === 'string') {
      const emailPayload: EmailPayload = { email };
      dispatch(resendCode(emailPayload));
    }
  }, [dispatch, signupEmail, jwtEmail]);

  const handleSubmit = useCallback(
    (values: { verificationCode: string }) => {
      setHasSubmitted(true);
      setErrorMessage('');
      const email = signupEmail || jwtEmail || dataParams.email;
      if (email && typeof email === 'string' && !hasVerified) {
        setHasVerified(true);
        const verifyPayload: VerifyPayload = {
          email: email.replaceAll(' ', '+'),
          ...values,
        };
        dispatch(verifyCode(verifyPayload));
      }
    },
    [
      setErrorMessage,
      signupEmail,
      jwtEmail,
      dispatch,
      dataParams.email,
      hasVerified,
    ],
  );

  useEffect(() => {
    if (hasSubmitted && hasVerifiedCode) {
      setHasSubmitted(false);
      history.push(LOADING_SCREEN);
    }
  }, [hasSubmitted, hasVerifiedCode, history]);

  useEffect(() => {
    // If we have all params from URL, redirect to loading page instead of auto-submitting
    if (isDataParamsExist) {
      // Redirect to email verification loading page with all params
      history.push(
        `/emailVerificationLoading?verificationCode=${
          dataParams.verificationCode
        }&email=${encodeURIComponent(
          dataParams.email!,
        )}&name=${encodeURIComponent(dataParams.name!)}`,
      );
    }
  }, [isDataParamsExist, dataParams, history]);

  // If we have params, we'll redirect to loading page, so show nothing or a loading state
  if (isDataParamsExist) {
    return null; // Will redirect immediately
  }

  return (
    <Container>
      <ContainerLeft>
        <Logo src={logo}></Logo>
      </ContainerLeft>
      <ContainerRight>
        <FormContainer>
          <FormTitle>{localizedStrings.verifyEmailAccount}</FormTitle>
          <FormSubtitle>
            {localizedStrings.sentYouAnEmail(
              (signupEmail || jwtEmail || 'your email') as string,
            )}
          </FormSubtitle>
          <Formik
            initialValues={{
              verificationCode: dataParams.verificationCode || '',
            }}
            onSubmit={handleSubmit}
            validationSchema={emailVerificationSchema}>
            <FormContent>
              <FormMessage>
                {localizedStrings.codeVerificationAcknowledgment1 + ' '}
                <FormLink onClick={onResend}>
                  {localizedStrings.codeVerificationAcknowledgment2}
                </FormLink>
                {' ' + localizedStrings.or + ' '}
                <FormLink onClick={onGoBack}>
                  {localizedStrings.goBack}
                </FormLink>
              </FormMessage>
            </FormContent>
          </Formik>
        </FormContainer>
      </ContainerRight>
    </Container>
  );
};

export default EmailVerification;
